import { useEffect, useState, useCallback } from "react";
import { PractitionerDirectory, Slot, Organization } from "../../shared/types";
import DateTimePicker from "./DateTimePicker";
import { useSlotTypes } from "../../hooks/useSlotTypes";
import { CalendarIcon, ClockIcon, MagnifyingGlassIcon, UserIcon } from "@heroicons/react/24/outline";
import AppointmentGroupSlot from "./AppointmentGroupSlot";
import Loading from "../Loading";
import { apiGet } from "src/utils/api";
import { Card } from "../Card";
import Button from "../Button";
import SelectInput from "../inputs/SelectInput";

type SlotsProps = {
  organization: Organization;
  selectedPractitioner: PractitionerDirectory;
  onSlotSelect: (slot: Slot) => void;
};

const Slots = ({ organization, selectedPractitioner, onSlotSelect }: SlotsProps) => {
  const { slotTypes, loading: loadingSlotTypes, error: slotTypesError } = useSlotTypes();
  const [ startDateTime, setStartDateTime ] = useState<Date>(new Date(new Date().setHours(0, 0, 0, 0)));
  const [ endDateTime, setEndDateTime ] = useState<Date>(new Date(new Date().setHours(23, 59, 59, 999)));
  const [ selectedSlotType, setSelectedSlotType ] = useState<string>("408443003");
  const [ slots, setSlots ] = useState<Slot[]>([]);
  const [ loading, setLoading ] = useState<boolean>(false);

  useEffect(() => {
    if (slotTypes.length > 0 && !slotTypes.find(st => st.value === selectedSlotType)) {
      setSelectedSlotType(slotTypes[0].value);
    }
  }, [slotTypes, selectedSlotType]);

  const handleFindAppointments = useCallback(async () => {
    try {
      setLoading(true);

      let url = `/api/${organization.id}/ehr/find-appointments?start=${startDateTime.toISOString()}&end=${endDateTime.toISOString()}`;
      if (selectedPractitioner) url += `&practitioner_id=${selectedPractitioner.practitioner_id}`;
      if (selectedPractitioner?.location_id) url += `&location_id=${selectedPractitioner.location_id}`;
      url += `&slot_type=${selectedSlotType}`;

      const response = await apiGet(url);
      const data = await response.json();
      if (!data.error) {
        setSlots(data);
        setLoading(false);
      } else {
        setLoading(false);
        setSlots([]);
      }
    } catch (error) {
      setLoading(false);
      setSlots([]);
    }
  }, [ startDateTime, endDateTime, selectedPractitioner, selectedSlotType, organization.id ]);

  const groupSlotsByDate = (slots: Slot[]) => {
    const grouped: { [key: string]: Slot[] } = {};
    slots.length > 0 && slots.forEach(slot => {
      const date = new Date(slot.start).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  };

  const groupedSlots = groupSlotsByDate(slots);

  useEffect(() => {
    handleFindAppointments();
  }, [ handleFindAppointments ]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 py-8">
      <Card className="p-6 shadow-xl rounded-2xl bg-white border-0">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Provider</h3>
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-brand-100 to-brand-200 rounded-full flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-6 h-6 text-brand-600" />
          </div>
          <div className="flex-grow">
            <p className="font-semibold text-lg text-gray-900">{selectedPractitioner.name}</p>
            <p className="text-sm text-gray-600">{selectedPractitioner.location}</p>
          </div>
        </div>
      </Card>

      <Card className="p-8 shadow-xl rounded-2xl bg-white border-0">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">Select Date Range</h3>
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <DateTimePicker
              startDate={startDateTime}
              endDate={endDateTime}
              onDateTimeChange={(start: Date, end: Date) => {
                setStartDateTime(start);
                setEndDateTime(end);
              }}
              label="Appointment Time Range"
            />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center pt-2">Filter by Appointment Type</h3>
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            {loadingSlotTypes ? (
              <div className="flex items-center justify-center py-2">
                <Loading message="Loading Appointment Types..." />
              </div>
            ) : slotTypesError ? (
              <div className="text-red-600 text-sm py-2">
                Error loading appointment types: {slotTypesError}
              </div>
            ) : (
              <SelectInput
                required={true}
                label="Appointment Type"
                placeholder="Select your required appointment type"
                value={selectedSlotType}
                onChange={(e) => setSelectedSlotType(e.target.value)}
                options={slotTypes.map((slotType) => ({ value: slotType.value, label: slotType.label }))}
                error={slotTypesError}
                helperText="Choose your required appointment type"
                icon={<CalendarIcon className="w-5 h-5 mr-2 text-gray-600" />}
              />
            )}
          </div>
        </div>
        <div className="flex justify-center mt-6">
          <Button
            text={loading ? "Searching..." : "Find Appointments"}
            onClick={handleFindAppointments}
            icon={<MagnifyingGlassIcon className="w-5 h-5" />}
            className="px-12 py-3 text-base font-medium w-full md:w-auto"
          />
        </div>
      </Card>

      <div className="bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Available Appointments
          </h3>
          {loading ? (
            <div className="flex justify-center mt-8">
              <Loading message="Loading Appointments..." />
            </div>
          )
          : (
            <>
              {slots.length > 0 ? (
                <div className="flex flex-col space-y-6">
                  {Object.entries(groupedSlots).map(([date, daySlots], index) => (
                    <AppointmentGroupSlot
                      key={date}
                      date={date}
                      daySlots={daySlots}
                      onSlotSelect={onSlotSelect}
                      loading={index === Object.entries(groupedSlots).length - 1 && loading}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ClockIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">No appointments available for the selected date range</p>
                  <p className="text-sm text-gray-400 mt-2">Try selecting a different date range or appointment type</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Slots;
